import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

import { editUnit } from "../../state/lists";

import { Button } from "../button";
import { LocalizedRuleLink, RuleWithIcon } from "../rules-index";

import "./GeneratedSpells.css";

function spellIdToFormattedMessageId(spellId) {
  const formattedSpellId = spellId
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/['’()!]/g, "")
    .replace(/ /g, "-");

  return `magic.spell.${formattedSpellId}`;
}

export const GeneratedSpells = ({
  availableLoresWithSpells,
  maxGeneratedSpellCount,
  showPageNumbers,
  maxSignatureSpells = 1,
  unitId,
  type,
  generatedSpells: initialGeneratedSpells,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { listId } = useParams();
  const [showGenerationList, setShowGenerationList] = useState(false);
  const [generatedSpells, setGeneratedSpells] = useState(
    initialGeneratedSpells ||
      Object.keys(availableLoresWithSpells).reduce(
        (initialState, loreId) => ({ ...initialState, [loreId]: [] }),
        {},
      ),
  );

  const handleSpellSelectionChange = (loreId, spellId, selected) => {
    if (selected) {
      setGeneratedSpells((generatedSpells) => {
        // Insert the newly selected spell at the correct index (signature spell first)
        if (availableLoresWithSpells[loreId][spellId].index === "signature") {
          return {
            ...generatedSpells,
            [loreId]: [spellId, ...generatedSpells[loreId]],
          };
        }
        for (let i = 0; i <= generatedSpells[loreId].length; i++) {
          const generatedSpellIndex =
            availableLoresWithSpells[loreId][generatedSpells[loreId][i]]?.index;
          if (
            i === generatedSpells[loreId].length ||
            (generatedSpellIndex !== "signature" &&
              generatedSpellIndex >
                availableLoresWithSpells[loreId][spellId].index)
          ) {
            return {
              ...generatedSpells,
              [loreId]: generatedSpells[loreId].toSpliced(i, 0, spellId),
            };
          }
        }
      });
    } else {
      setGeneratedSpells((generatedSpells) => ({
        ...generatedSpells,
        [loreId]: generatedSpells[loreId].filter((id) => id !== spellId),
      }));
    }
  };

  useEffect(() => {
    if ((listId, type, unitId)) {
      dispatch(
        editUnit({
          listId,
          type,
          unitId,
          generatedSpells,
        }),
      );
    }
  }, [generatedSpells, dispatch, listId, type, unitId]);

  let generatedSpellCount = 0;
  let signatureSpellsGenerated = 0;
  // let loresWithSpellCount = 0;

  for (const loreId in generatedSpells) {
    generatedSpellCount += generatedSpells[loreId].length;

    for (let j = 0; j < generatedSpells[loreId].length; j++) {
      const spellId = generatedSpells[loreId][j];

      if (
        availableLoresWithSpells[loreId] &&
        availableLoresWithSpells[loreId][spellId]?.index === "signature"
      ) {
        signatureSpellsGenerated++;
      }
    }
  }

  // Object.keys(generatedSpells).forEach((loreId) => {
  //   const spells = generatedSpells[loreId];

  //   if (spells.length > 0) {
  //     const isSignatureOnlyLore =
  //       availableLoresWithSpells[loreId] &&
  //       !Object.values(availableLoresWithSpells[loreId]).find(
  //         (spell) => spell.index !== "signature",
  //       );

  //     if (!isSignatureOnlyLore) {
  //       loresWithSpellCount++;
  //     }
  //   }
  // });

  return (
    <div className="generated-spells__wrapper">
      <p className="generated-spells__header">
        <FormattedMessage id="misc.generatedSpells" />
        {` (${generatedSpellCount}/${maxGeneratedSpellCount}) `}
        <Button
          type="text"
          color="dark"
          className="generated-spells__edit-button"
          label={intl.formatMessage({ id: "misc.editGeneratedSpells" })}
          onClick={() =>
            setShowGenerationList((showGenerationList) => !showGenerationList)
          }
          icon={showGenerationList ? "edit-off" : "edit"}
        />
      </p>
      {showGenerationList ? (
        <ul className="generated-spells__spells-selection-list">
          {Object.entries(availableLoresWithSpells).map(([loreId, spells]) => {
            if (!generatedSpells[loreId]) {
              return null;
            }

            return (
              <li key={loreId}>
                <ul>
                  <div className="generated-spells__list-header">
                    <FormattedMessage id={`magic.lore.${loreId}`} />
                  </div>
                  {Object.entries(spells).map(([spellId, spell]) => {
                    const spellIsGenerated =
                      generatedSpells[loreId].find((id) => id === spellId) !==
                      undefined;

                    return (
                      <li key={spellId}>
                        <label className="generated-spells__spell-label">
                          <input
                            type="checkbox"
                            checked={spellIsGenerated}
                            disabled={
                              (generatedSpellCount === maxGeneratedSpellCount &&
                                !spellIsGenerated) ||
                              (spell.index === "signature" &&
                                signatureSpellsGenerated >=
                                  maxSignatureSpells &&
                                !spellIsGenerated)
                            }
                            onChange={(event) => {
                              handleSpellSelectionChange(
                                loreId,
                                spellId,
                                event.target.checked,
                              );
                            }}
                          />
                          <span className="generated-spells__rules-wrapper">
                            <span className="generated-spells__spell-index">
                              {spell.index === "signature"
                                ? intl.formatMessage({
                                    id: "misc.signatureAbbr",
                                  })
                                : spell.index}
                            </span>
                            <FormattedMessage
                              id={spellIdToFormattedMessageId(spellId)}
                            />
                            <RuleWithIcon
                              name={spellId}
                              armyComposition={spell.armyComposition}
                              isDark
                              className="generated-spells__rule-icon"
                            />
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      ) : (
        Object.values(generatedSpells).map((spells) =>
          spells.map((spellId) => (
            <span className="generated-spells__spell-rule" key={spellId}>
              <LocalizedRuleLink
                ruleName={spellId}
                formattedMessageId={spellIdToFormattedMessageId(spellId)}
                showPageNumber={showPageNumbers}
              />
            </span>
          )),
        )
      )}
    </div>
  );
};

GeneratedSpells.propTypes = {
  availableLoresWithSpells: PropTypes.objectOf(PropTypes.object).isRequired,
  maxGeneratedSpellCount: PropTypes.number.isRequired,
  showPageNumbers: PropTypes.bool,
};
